import React, { useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Copy,
  Download,
  FileSearch,
  FileText,
  Filter,
  Globe2,
  Languages,
  LockKeyhole,
  Server,
  ShieldCheck,
  UploadCloud,
  Wifi
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  analyzeHar,
  formatBytes,
  formatMs,
  truncateMiddle,
  type DomainSummary,
  type Finding,
  type HarAnalysis,
  type NetworkEntry,
  type Severity
} from "./harUtils";
import { sampleHarText } from "./sampleHar";
import "./styles.css";

declare global {
  interface Window {
    __harscopeRoot?: Root;
  }
}

type Language = "en" | "ko";
type FilterKey = "all" | "errors" | "slow" | "thirdParty" | "sensitive";

const labels = {
  en: {
    local: "Local-first",
    sample: "Load sample",
    analyze: "Analyze HAR",
    upload: "Upload .har",
    paste: "Paste HAR JSON",
    copyReport: "Copy report",
    downloadReport: "Download .md",
    noUpload: "Your HAR stays in this browser session.",
    riskHigh: "Sensitive markers found. Review before sharing.",
    riskMedium: "Review warnings before sending this trace.",
    riskLow: "No high-risk markers detected in this pass.",
    requests: "Requests",
    transfer: "Transfer",
    span: "Span",
    errors: "Errors",
    slow: "Slow",
    sensitive: "Sensitive",
    thirdParty: "Third-party",
    domains: "Domains",
    https: "HTTPS",
    waterfall: "Request waterfall",
    findings: "Findings",
    supportReport: "Support report",
    domainMap: "Domain map",
    selected: "Selected request",
    all: "All",
    parseError: "Could not analyze this HAR",
    copied: "Report copied",
    drag: "Drop a HAR file here",
    choose: "Choose file",
    reportHint: "Redacted query values and header values are not included.",
    emptyFindings: "No findings for the current trace.",
    entryHint: "Pick a request row to inspect timing and risk markers.",
    reportReady: "Ticket-ready Markdown"
  },
  ko: {
    local: "로컬 처리",
    sample: "샘플 불러오기",
    analyze: "HAR 분석",
    upload: ".har 업로드",
    paste: "HAR JSON 붙여넣기",
    copyReport: "리포트 복사",
    downloadReport: ".md 다운로드",
    noUpload: "HAR 파일은 현재 브라우저 세션 안에서만 처리됩니다.",
    riskHigh: "민감정보 신호가 있습니다. 공유 전에 확인하세요.",
    riskMedium: "지원팀에 보내기 전에 경고를 검토하세요.",
    riskLow: "이번 검사에서 고위험 신호는 보이지 않습니다.",
    requests: "요청",
    transfer: "전송량",
    span: "구간",
    errors: "오류",
    slow: "느림",
    sensitive: "민감",
    thirdParty: "외부",
    domains: "도메인",
    https: "HTTPS",
    waterfall: "요청 워터폴",
    findings: "진단 결과",
    supportReport: "지원 리포트",
    domainMap: "도메인 맵",
    selected: "선택 요청",
    all: "전체",
    parseError: "이 HAR를 분석하지 못했습니다",
    copied: "리포트 복사됨",
    drag: "HAR 파일을 여기에 놓기",
    choose: "파일 선택",
    reportHint: "쿼리 값과 헤더 값은 리포트에 포함하지 않습니다.",
    emptyFindings: "현재 트레이스에는 표시할 진단 결과가 없습니다.",
    entryHint: "요청 행을 선택하면 타이밍과 위험 신호를 볼 수 있습니다.",
    reportReady: "티켓용 Markdown"
  }
};

const filterMeta: Array<{ key: FilterKey; icon: LucideIcon; en: string; ko: string }> = [
  { key: "all", icon: Filter, en: "All", ko: "전체" },
  { key: "errors", icon: AlertTriangle, en: "Errors", ko: "오류" },
  { key: "slow", icon: Clock3, en: "Slow", ko: "느림" },
  { key: "thirdParty", icon: Globe2, en: "Third-party", ko: "외부" },
  { key: "sensitive", icon: LockKeyhole, en: "Sensitive", ko: "민감" }
];

function App() {
  const [language, setLanguage] = useState<Language>("en");
  const [rawInput, setRawInput] = useState(sampleHarText);
  const [sourceName, setSourceName] = useState("sample-support-trace.har");
  const [analysis, setAnalysis] = useState<HarAnalysis>(() =>
    analyzeHar(sampleHarText, "sample-support-trace.har")
  );
  const [parseError, setParseError] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [selectedEntryId, setSelectedEntryId] = useState<string>(analysis.entries[0]?.id ?? "");
  const [copyState, setCopyState] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const t = labels[language];

  const filteredEntries = useMemo(
    () => filterEntries(analysis.entries, activeFilter),
    [analysis.entries, activeFilter]
  );

  const selectedEntry =
    analysis.entries.find((entry) => entry.id === selectedEntryId) ?? filteredEntries[0] ?? analysis.entries[0];

  const filterCounts: Record<FilterKey, number> = {
    all: analysis.entries.length,
    errors: analysis.metrics.errors,
    slow: analysis.metrics.slowRequests,
    thirdParty: analysis.metrics.thirdPartyRequests,
    sensitive: analysis.metrics.sensitiveRequests
  };

  function runAnalysis(text: string, name = sourceName) {
    try {
      const next = analyzeHar(text, name);
      setAnalysis(next);
      setParseError("");
      setSelectedEntryId(next.entries[0]?.id ?? "");
      setActiveFilter("all");
    } catch (error) {
      setParseError(error instanceof Error ? error.message : "Unknown parse error");
    }
  }

  async function handleFile(file: File) {
    const text = await file.text();
    setRawInput(text);
    setSourceName(file.name);
    runAnalysis(text, file.name);
  }

  async function copyReport() {
    let copied = false;

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(analysis.report);
        copied = true;
      } catch {
        copied = false;
      }
    }

    if (!copied) {
      copied = copyTextWithSelection(analysis.report);
    }

    if (copied) {
      setCopyState(t.copied);
      window.setTimeout(() => setCopyState(""), 1600);
    } else {
      setCopyState("Copy failed");
    }
  }

  function downloadReport() {
    const blob = new Blob([analysis.report], { type: "text/markdown;charset=utf-8" });
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = `${sourceName.replace(/\.[^.]+$/, "") || "har-report"}-report.md`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(href);
  }

  return (
    <div className="app-shell">
      <header className="command-bar">
        <div className="brand-lockup">
          <div className="brand-mark">
            <Wifi size={19} />
          </div>
          <div>
            <p>{t.local}</p>
            <h1>HARScope Inspector</h1>
          </div>
        </div>
        <div className="command-actions">
          <button className="ghost-button" onClick={() => setLanguage(language === "en" ? "ko" : "en")}>
            <Languages size={17} />
            {language === "en" ? "한국어" : "English"}
          </button>
          <button
            className="ghost-button"
            onClick={() => {
              setRawInput(sampleHarText);
              setSourceName("sample-support-trace.har");
              runAnalysis(sampleHarText, "sample-support-trace.har");
            }}
          >
            <FileSearch size={17} />
            {t.sample}
          </button>
          <button className="primary-button" onClick={() => runAnalysis(rawInput, sourceName)}>
            <Activity size={17} />
            {t.analyze}
          </button>
        </div>
      </header>

      <main className="workspace">
        <section className="left-rail">
          <PanelTitle icon={UploadCloud} title={t.upload} meta={sourceName} />
          <div
            className={`drop-zone ${dragActive ? "is-active" : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragActive(false);
              const [file] = Array.from(event.dataTransfer.files);
              if (file) void handleFile(file);
            }}
          >
            <UploadCloud size={28} />
            <strong>{t.drag}</strong>
            <span>{t.noUpload}</span>
            <button className="mini-button" onClick={() => fileInputRef.current?.click()}>
              {t.choose}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".har,application/json,.json"
              onChange={(event) => {
                const [file] = Array.from(event.target.files ?? []);
                if (file) void handleFile(file);
              }}
            />
          </div>

          <label className="editor-label" htmlFor="har-input">
            {t.paste}
          </label>
          <textarea
            id="har-input"
            className="har-editor"
            value={rawInput}
            onChange={(event) => setRawInput(event.target.value)}
            spellCheck={false}
          />

          {parseError ? (
            <div className="notice high">
              <AlertTriangle size={18} />
              <div>
                <strong>{t.parseError}</strong>
                <span>{parseError}</span>
              </div>
            </div>
          ) : (
            <RiskNotice severity={analysis.metrics.riskLevel} text={riskText(t, analysis.metrics.riskLevel)} />
          )}
        </section>

        <section className="main-console">
          <div className="metrics-grid">
            <MetricCard icon={FileText} label={t.requests} value={analysis.metrics.totalRequests.toString()} />
            <MetricCard icon={Server} label={t.transfer} value={formatBytes(analysis.metrics.totalBytes)} />
            <MetricCard icon={Clock3} label={t.span} value={formatMs(analysis.metrics.totalSpan)} />
            <MetricCard icon={AlertTriangle} label={t.errors} value={analysis.metrics.errors.toString()} tone="warn" />
            <MetricCard icon={LockKeyhole} label={t.sensitive} value={analysis.metrics.sensitiveRequests.toString()} tone="danger" />
            <MetricCard icon={ShieldCheck} label={t.https} value={`${Math.round(analysis.metrics.httpsRatio * 100)}%`} tone="ok" />
          </div>

          <div className="console-section">
            <div className="section-toolbar">
              <PanelTitle icon={Activity} title={t.waterfall} meta={`${filteredEntries.length}/${analysis.entries.length}`} />
              <div className="filter-strip" role="tablist" aria-label="Request filters">
                {filterMeta.map(({ key, icon: Icon, en, ko }) => (
                  <button
                    key={key}
                    className={activeFilter === key ? "filter-button active" : "filter-button"}
                    onClick={() => setActiveFilter(key)}
                    role="tab"
                    aria-selected={activeFilter === key}
                  >
                    <Icon size={14} />
                    <span>{language === "en" ? en : ko}</span>
                    <b>{filterCounts[key]}</b>
                  </button>
                ))}
              </div>
            </div>
            <Waterfall
              entries={filteredEntries}
              totalSpan={analysis.metrics.totalSpan}
              selectedId={selectedEntry?.id}
              onSelect={setSelectedEntryId}
            />
          </div>

          <div className="console-split">
            <DomainTable domains={analysis.domains} language={language} />
            <EntryDetails entry={selectedEntry} language={language} />
          </div>
        </section>

        <aside className="right-rail">
          <div className="rail-header">
            <PanelTitle icon={ClipboardCheck} title={t.supportReport} meta={t.reportReady} />
            <div className="report-actions">
              <button className="icon-button" onClick={copyReport} aria-label={t.copyReport} title={t.copyReport}>
                <Copy size={16} />
              </button>
              <button className="icon-button" onClick={downloadReport} aria-label={t.downloadReport} title={t.downloadReport}>
                <Download size={16} />
              </button>
            </div>
          </div>
          {copyState ? <div className="copy-state">{copyState}</div> : <p className="rail-note">{t.reportHint}</p>}
          <pre className="report-preview">{analysis.report}</pre>

          <div className="findings-block">
            <PanelTitle icon={AlertTriangle} title={t.findings} meta={`${analysis.findings.length}`} />
            {analysis.findings.length ? (
              <div className="finding-list">
                {analysis.findings.slice(0, 10).map((finding) => (
                  <FindingRow key={finding.id} finding={finding} />
                ))}
              </div>
            ) : (
              <p className="empty-state">{t.emptyFindings}</p>
            )}
          </div>
        </aside>
      </main>
      <footer className="site-footer">
        <span>Independent HAR analyzer. Files are processed in the browser and are not uploaded.</span>
        <nav aria-label="Site policies">
          <a data-google-vignette="false" href="/privacy.html">Privacy</a>
          <a data-google-vignette="false" href="/about.html">About</a>
        </nav>
      </footer>
    </div>
  );
}

function PanelTitle({ icon: Icon, title, meta }: { icon: LucideIcon; title: string; meta?: string }) {
  return (
    <div className="panel-title">
      <Icon size={18} />
      <div>
        <h2>{title}</h2>
        {meta ? <span>{meta}</span> : null}
      </div>
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  tone = "neutral"
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  tone?: "neutral" | "warn" | "danger" | "ok";
}) {
  return (
    <div className={`metric-card ${tone}`}>
      <Icon size={18} />
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function RiskNotice({ severity, text }: { severity: Severity; text: string }) {
  const Icon = severity === "low" ? CheckCircle2 : AlertTriangle;
  return (
    <div className={`notice ${severity}`}>
      <Icon size={18} />
      <div>
        <strong>{severity.toUpperCase()} TRACE RISK</strong>
        <span>{text}</span>
      </div>
    </div>
  );
}

function Waterfall({
  entries,
  totalSpan,
  selectedId,
  onSelect
}: {
  entries: NetworkEntry[];
  totalSpan: number;
  selectedId?: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="waterfall-list">
      {entries.map((entry) => {
        const left = totalSpan ? (entry.startOffset / totalSpan) * 100 : 0;
        const width = totalSpan ? Math.max(2.2, (entry.time / totalSpan) * 100) : 10;
        return (
          <button
            className={`waterfall-row ${entry.id === selectedId ? "selected" : ""}`}
            key={entry.id}
            onClick={() => onSelect(entry.id)}
          >
            <span className={`status-code ${statusTone(entry.status)}`}>{entry.status || "?"}</span>
            <span className="method">{entry.method}</span>
            <span className="request-name" title={entry.displayUrl}>
              {truncateMiddle(entry.displayUrl, 88)}
            </span>
            <span className="timeline-track" aria-hidden="true">
              <i
                className={`timeline-bar ${entry.isError ? "error" : entry.isSlow ? "slow" : "ok"}`}
                style={{ left: `${left}%`, width: `${Math.min(width, 100 - left)}%` }}
              />
            </span>
            <span className="duration">{formatMs(entry.time)}</span>
          </button>
        );
      })}
    </div>
  );
}

function DomainTable({ domains, language }: { domains: DomainSummary[]; language: Language }) {
  return (
    <div className="domain-panel">
      <PanelTitle icon={Globe2} title={language === "en" ? "Domain map" : "도메인 맵"} meta={`${domains.length}`} />
      <div className="domain-table">
        {domains.slice(0, 8).map((domain) => (
          <div className="domain-row" key={domain.host}>
            <div>
              <strong>{domain.host}</strong>
              <span>{domain.thirdParty ? "third-party" : "primary"}</span>
            </div>
            <b>{domain.requests}</b>
            <span>{formatBytes(domain.bytes)}</span>
            <span>{domain.errors} err</span>
            <span>{formatMs(domain.slowest)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EntryDetails({ entry, language }: { entry?: NetworkEntry; language: Language }) {
  if (!entry) {
    return (
      <div className="entry-panel">
        <PanelTitle icon={FileSearch} title={language === "en" ? "Selected request" : "선택 요청"} />
        <p className="empty-state">{language === "en" ? labels.en.entryHint : labels.ko.entryHint}</p>
      </div>
    );
  }

  const timingValues = Object.entries(entry.timings).filter(([, value]) => value > 0);

  return (
    <div className="entry-panel">
      <PanelTitle icon={FileSearch} title={language === "en" ? "Selected request" : "선택 요청"} meta={entry.host} />
      <div className="entry-summary">
        <span className={`status-code ${statusTone(entry.status)}`}>{entry.status || "?"}</span>
        <strong>{entry.method}</strong>
        <span>{formatMs(entry.time)}</span>
        <span>{formatBytes(entry.size)}</span>
      </div>
      <p className="entry-url">{entry.displayUrl}</p>
      <div className="timing-grid">
        {timingValues.map(([key, value]) => (
          <div key={key}>
            <span>{key}</span>
            <b>{formatMs(value)}</b>
          </div>
        ))}
      </div>
      <div className="risk-chips">
        <span>{entry.cacheStatus}</span>
        {entry.isThirdParty ? <span>third-party</span> : <span>primary</span>}
        {entry.sensitiveHits.length ? <span className="danger-chip">{entry.sensitiveHits.length} sensitive</span> : null}
      </div>
      {entry.sensitiveHits.length ? (
        <ul className="sensitive-list">
          {entry.sensitiveHits.map((hit) => (
            <li key={`${hit.area}-${hit.name}-${hit.reason}`}>
              <b>{hit.area}</b>
              <span>{hit.name}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function FindingRow({ finding }: { finding: Finding }) {
  const Icon = finding.severity === "low" ? CheckCircle2 : AlertTriangle;
  return (
    <article className={`finding-row ${finding.severity}`}>
      <Icon size={16} />
      <div>
        <span>{finding.category}</span>
        <strong>{finding.title}</strong>
        <p>{finding.detail}</p>
      </div>
    </article>
  );
}

function filterEntries(entries: NetworkEntry[], filter: FilterKey): NetworkEntry[] {
  if (filter === "errors") return entries.filter((entry) => entry.isError);
  if (filter === "slow") return entries.filter((entry) => entry.isSlow);
  if (filter === "thirdParty") return entries.filter((entry) => entry.isThirdParty);
  if (filter === "sensitive") return entries.filter((entry) => entry.sensitiveHits.length);
  return entries;
}

function statusTone(status: number): string {
  if (status >= 500) return "status-high";
  if (status >= 400) return "status-warn";
  if (status >= 300) return "status-redirect";
  if (status >= 200) return "status-ok";
  return "status-muted";
}

function riskText(t: (typeof labels)[Language], severity: Severity): string {
  if (severity === "high") return t.riskHigh;
  if (severity === "medium") return t.riskMedium;
  return t.riskLow;
}

function copyTextWithSelection(text: string): boolean {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  return copied;
}

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

const root = window.__harscopeRoot ?? createRoot(rootElement);
window.__harscopeRoot = root;

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
